<template>
  <modal
    height="700px"
    width="600px"
    bodyPadding="0"
    :title="title"
    @close="close"
    :read-article-url="readArticleUrl"
  >
    <div class="dialog-wrapper">
      <div class="dialog-create-from-template__body-inner">
        <div class="template-select-dialog__body-inner">
          <div class="search-wrapper">
            <ui-input
              v-model="search"
              hideDetails="1"
              :placeholder="t('Search')"
              prepend-inner-icon="fas fa-search"
            />
          </div>
          <div class="scroll-block">
            <template v-if="itemsIntegrated.length">
              <div class="subtitle-template">
                {{ t('Integrated') }}
              </div>
              <div class="list-tile">
                <button
                  type="button"
                  v-for="item in computedItemsIntegrated"
                  :key="item.id"
                  @click="onUpdateActiveItem(item.id)"
                  :class="{
                    integration: true,
                    'integration__active': activeItem === item.id,
                  }"
                >
                  <img
                    :alt="item.name"
                    :style="integrationsData[item.integrationType]?.img?.style"
                    :src="
                      '/images/integrations/' +
                      integrationsData[item.integrationType]?.img?.src
                    "
                  />
                </button>
                <div class="ml-8 mr-8" v-if="!computedItemsIntegrated.length">
                  {{ t('No data available') }}
                </div>
              </div>
            </template>

            <div class="subtitle-template" v-if="itemsIntegrated.length">
              {{ t('Others') }}
            </div>
            <div class="list-tile" v-if="computedItemsTileOthers.length">
              <button
                type="button"
                v-for="item in computedItemsTileOthers"
                :key="item.id"
                @click="onUpdateActiveItem(item.id)"
                :class="{
                  integration: true,
                  'integration__active': activeItem === item.id,
                }"
              >
                <img
                  :alt="item.name"
                  :style="networkListInTileFormat[item.name]?.style"
                  :src="
                    '/images/create-from-template/' +
                    networkListInTileFormat[item.name]?.img?.src
                  "
                />
              </button>
            </div>

            <div class="list-others">
              <div
                v-for="item in computedItemsOthers"
                :key="item.id"
                :class="{
                  'list-item': true,
                  'list-item__active': activeItem === item.id,
                }"
                @click="onUpdateActiveItem(item.id)"
              >
                {{ item.name }}
              </div>
              <div v-if="!computedItemsOthers.length && !computedItemsTileOthers.length">
                {{ t('No data available') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <section class="footer xs12">
        <ui-button :text="t('Cancel')" icon="fas fa-times" @click="close" />

        <ui-button
          :text="t('Next')"
          icon-append="fas fa-arrow-right"
          button-style="accent"
          :disabled="!activeItem"
          @click="onConfirm"
        />
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n, useIntegrations } from '@/composables';

type TemplateListItem = {
  id: string;
  name: string;
  integrationType?: string;
};

type TileFormatEntry = {
  style?: Record<string, string>;
  img: { src: string };
};

const props = withDefaults(
  defineProps<{
    itemsOthers?: TemplateListItem[];
    itemsOthersTile?: TemplateListItem[];
    itemsIntegrated?: TemplateListItem[];
    readArticleUrl?: string;
    title?: string;
    hasHelp?: boolean;
    networkListInTileFormat?: Record<string, TileFormatEntry>;
  }>(),
  {
    itemsOthers: () => [],
    itemsOthersTile: () => [],
    itemsIntegrated: () => [],
    hasHelp: true,
    networkListInTileFormat: () => ({}),
  },
);

const emit = defineEmits<{
  close: [];
  confirm: [id: string];
  'update:activeItem': [id: string];
}>();

const { t } = useI18n();
const { integrationsData } = useIntegrations();

const search = ref('');
const activeItem = ref<string | null>(null);

const computedItemsOthers = computed(() =>
  props.itemsOthers.filter(item =>
    item.name.toLowerCase().includes(search.value.toLowerCase()),
  ),
);

const computedItemsTileOthers = computed(() =>
  props.itemsOthersTile.filter(item =>
    item.name.toLowerCase().includes(search.value.toLowerCase()),
  ),
);

const computedItemsIntegrated = computed(() =>
  props.itemsIntegrated.filter(item => {
    const integrationInstance = item.integrationType
      ? integrationsData.value[item.integrationType]
      : undefined;
    return (
      item.name.toLowerCase().includes(search.value.toLowerCase()) &&
      integrationInstance &&
      integrationInstance.img
    );
  }),
);

const onUpdateActiveItem = (value: string) => {
  activeItem.value = value;
  emit('update:activeItem', value);
};

const onConfirm = () => {
  if (!activeItem.value) return;
  emit('confirm', activeItem.value);
};

const close = () => {
  emit('close');
};
</script>

<style lang="scss">
.dialog-create-from-template {
  height: 100%;
  max-height: 700px !important;
  .v-card {
    height: 100%;
    max-height: 700px;
  }
  &__body {
    background: var(--color-gray5);
  }

  &__body-inner {
    height: 100%;
  }
}
</style>

<style lang="scss" scoped>
@import '@/styles/dialogs/report-modals';

.footer {
  display: flex;
  justify-content: flex-end;
}
.search-wrapper {
  padding: 16px 16px 12px 16px;
}

.subtitle-template {
  font-size: 16px;
  font-weight: 800;
  padding: 0 16px 8px;
}

.list-others {
  padding: 0 16px;
  background: var(--color-white);
}

.list-item {
  font-size: 13px;
  cursor: pointer;
  border-top: 1px solid var(--color-gray10);
  padding: 8px 0;
  &:first-child {
    border-top: none;
  }
  &__active {
    background: var(--color-green10);
  }
}

.list-tile {
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  padding: 0 8px 8px;
}

.integration {
  width: calc(25% - 16px);
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--color-white);
  border-radius: 8px;
  height: 56px;
  margin: 0 8px 8px;
  cursor: pointer;
  border: 2px solid var(--color-gray5);
  &__active {
    box-shadow: 0 0 8px 0 var(--color-green20);
    border: 2px solid var(--color-green);
  }

  img {
    max-width: 90%;
    max-height: 80%;
  }
  @media all and (max-width: 600px) {
    width: calc(33.3333333333333% - 16px);
  }
  @media all and (max-width: 500px) {
    width: calc(50% - 16px);
  }
}
.scroll-block {
  overflow-y: auto;
  height: calc(100% - 60px);
}
</style>
